import React, { useEffect, useState } from "react";
import { adminApi } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { ShieldAlert, Inbox, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function AdminPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [inbox, setInbox] = useState(null);
  const [inboxLoading, setInboxLoading] = useState(false);

  useEffect(() => {
    adminApi.status().then(setData).catch((err) => {
      console.debug("[AdminPanel] status fetch failed", err);
      setData({ is_admin: false });
    });
  }, []);

  const loadInbox = async () => {
    try {
      setInboxLoading(true);
      const r = await adminApi.contactSubmissions(50);
      setInbox(r);
    } catch (err) {
      console.error("[AdminPanel] inbox load failed", err);
      toast.error("Failed to load contact inbox");
    } finally { setInboxLoading(false); }
  };

  useEffect(() => {
    if (data?.is_admin) loadInbox();
  }, [data?.is_admin]);

  if (!data || !data.is_admin) return null;

  const toggle = async (enabled) => {
    try {
      setBusy(true);
      await adminApi.setLiveTrading(enabled);
      setData((d) => ({ ...d, live_trading_enabled: enabled }));
      toast.success(`Live trading ${enabled ? "enabled" : "disabled"} platform-wide`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-panel" className="aq-card p-4 border-amber-500/30 bg-amber-500/5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-amber-400" />
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-200">Operator controls</div>
        </div>
        <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-200">
          Admin
        </span>
      </div>

      <div className="mt-3 rounded-md bg-black/40 border border-white/5 p-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Live trading platform-wide</div>
            <div className="mt-1 text-xs text-slate-300">
              {data.live_trading_enabled ? "Real-money Alpaca mode is ON for all users." : "All users limited to Alpaca paper mode."}
            </div>
          </div>
          <Switch
            data-testid="admin-live-trading-toggle"
            checked={!!data.live_trading_enabled}
            disabled={busy}
            onCheckedChange={toggle}
          />
        </div>
      </div>

      <div className="mt-2 text-[10px] font-mono text-slate-500">
        Bot scheduler: {data.bot_scheduler?.enabled ? `ON · every ${data.bot_scheduler.interval_seconds}s` : "OFF"}
      </div>

      {/* Contact submissions inbox */}
      <div className="mt-4 rounded-md bg-black/40 border border-white/5 p-3" data-testid="admin-contact-inbox">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <Inbox className="w-3.5 h-3.5 text-amber-300" />
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
              Contact inbox {inbox?.count !== undefined ? `· ${inbox.count}` : ""}
            </div>
          </div>
          <button
            data-testid="admin-inbox-refresh"
            onClick={loadInbox}
            disabled={inboxLoading}
            className="text-[10px] font-mono uppercase tracking-widest text-slate-400 hover:text-white inline-flex items-center gap-1"
          >
            <RefreshCw className={`w-3 h-3 ${inboxLoading ? "animate-spin" : ""}`} />
            {inboxLoading ? "loading" : "refresh"}
          </button>
        </div>
        <div className="mt-3 max-h-72 overflow-y-auto aq-scroll space-y-2">
          {!inbox || inbox.submissions.length === 0 ? (
            <div className="text-center text-slate-500 text-xs font-mono py-6">
              No contact submissions yet.
            </div>
          ) : (
            inbox.submissions.map((s) => (
              <div key={s.id} data-testid={`admin-inbox-row-${s.id}`} className="rounded border border-white/5 bg-black/30 p-2.5">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-display font-bold text-sm text-slate-100 truncate">{s.full_name}</div>
                  <div className="font-mono text-[10px] text-slate-500 shrink-0">
                    {s.created_at ? new Date(s.created_at).toLocaleString() : ""}
                  </div>
                </div>
                <div className="mt-0.5 flex items-center gap-2 text-[11px] text-slate-400">
                  <a href={`mailto:${s.email}`} className="text-emerald-300 hover:underline truncate">{s.email}</a>
                  {s.phone && <span className="font-mono">· {s.phone}</span>}
                  {s.delivered ? (
                    <span className="ml-auto text-[9px] font-mono uppercase tracking-widest text-emerald-300">sent</span>
                  ) : (
                    <span className="ml-auto text-[9px] font-mono uppercase tracking-widest text-amber-300">stored</span>
                  )}
                </div>
                <div className="mt-1.5 text-[12px] text-slate-300 leading-snug whitespace-pre-wrap">{s.message}</div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
